import { useId } from "react";
import { cn } from "@/lib/utils";

const C_PATH = "M64.85 35.15A21 21 0 1 0 64.85 64.85";
const PALETTES = {
  color: { face: ["#DDF6EC", "#B9EBD7", "#87B8A5"], c: "#142F39", ring: "#FFFFFF" },
  night: { face: ["#2A5463", "#1C3E4B", "#142F39"], c: "#B9EBD7", ring: "#B9EBD7" },
};

const TEETH = Array.from({ length: 72 }, (_, i) => (i * 360) / 72);

const Teeth = () => (
  <g>
    {TEETH.map((a) => (
      <line key={a} x1="50" y1="1.2" x2="50" y2="5.2" stroke="#A8893F" strokeWidth="1.7" strokeLinecap="round" transform={`rotate(${a} 50 50)`} />
    ))}
  </g>
);

export const CoinMark = ({ size = 40, variant = "color", reeded, className = "", ...rest }) => {
  const uid = useId().replace(/:/g, "");
  const withTeeth = reeded ?? size >= 48;
  if (variant === "mono") {
    return (
      <svg viewBox="0 0 100 100" width={size} height={size} className={className} role="img" aria-label="CASHMY" {...rest}>
        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="5" />
        <path d={C_PATH} fill="none" stroke="currentColor" strokeWidth="12" strokeLinecap="round" />
      </svg>
    );
  }
  const p = PALETTES[variant];
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className} role="img" aria-label="CASHMY" {...rest}>
      <defs>
        <linearGradient id={`f${uid}`} x1="20" y1="8" x2="82" y2="94" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={p.face[0]} />
          <stop offset="0.5" stopColor={p.face[1]} />
          <stop offset="1" stopColor={p.face[2]} />
        </linearGradient>
        <linearGradient id={`r${uid}`} x1="10" y1="10" x2="90" y2="90" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#EBD9A6" />
          <stop offset="1" stopColor="#C9A95E" />
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="47" fill={`url(#f${uid})`} />
      {withTeeth && <circle cx="50" cy="50" r="49.5" fill={`url(#r${uid})`} />}
      {withTeeth && <Teeth />}
      <circle cx="50" cy="50" r={withTeeth ? 44.5 : 47} fill={`url(#f${uid})`} />
      <circle cx="50" cy="50" r={withTeeth ? 44 : 46} fill="none" stroke={`url(#r${uid})`} strokeWidth="2.5" />
      <circle cx="50" cy="50" r="38.5" fill="none" stroke={p.ring} strokeOpacity="0.55" strokeWidth="1.4" />
      <path d={C_PATH} fill="none" stroke="#0B1D24" strokeOpacity="0.22" strokeWidth="12" strokeLinecap="round" transform="translate(1.2 2)" />
      <path d={C_PATH} fill="none" stroke={p.c} strokeWidth="12" strokeLinecap="round" />
      <ellipse cx="36" cy="25" rx="17" ry="7.5" fill="#FFFFFF" fillOpacity="0.32" transform="rotate(-28 36 25)" />
    </svg>
  );
};

export const Coin3D = ({ size = 42, spin = true, className = "" }) => {
  const depth = Math.max(4, Math.round(size * 0.14));
  const layers = 12;
  return (
    <div className={cn("coin3d shrink-0", className)} style={{ width: size, height: size }} data-testid="coin-3d">
      <div className={cn("coin3d-inner", spin && "coin3d-spin")}>
        {Array.from({ length: layers }, (_, i) => (
          <span key={i} className="coin3d-edge" style={{ transform: `translateZ(${-depth / 2 + (depth * i) / (layers - 1)}px)` }} />
        ))}
        <div className="coin3d-face" style={{ transform: `translateZ(${depth / 2 + 0.5}px)` }}>
          <CoinMark size={size} reeded /><span className="coin3d-shine" />
        </div>
        <div className="coin3d-face" style={{ transform: `rotateY(180deg) translateZ(${depth / 2 + 0.5}px)` }}>
          <CoinMark size={size} reeded /><span className="coin3d-shine" />
        </div>
      </div>
    </div>
  );
};

export const Wordmark = ({ className = "", name = "CASHMY" }) => {
  const split = name.toUpperCase().endsWith("MY") ? [name.slice(0, -2), name.slice(-2)] : [name, ""];
  return (
    <span className={cn("font-heading font-extrabold tracking-[0.1em]", className)}>
      {split[0]}
      <span className="text-sage">{split[1]}</span>
    </span>
  );
};

export const Logo = ({ layout = "horizontal", size = 36, variant = "color", className = "", textClassName = "", coinClassName = "" }) => (
  <span
    className={cn("inline-flex items-center", layout === "vertical" ? "flex-col gap-3 text-center" : "gap-2.5", className)}
    data-testid="brand-logo"
  >
    <CoinMark size={size} variant={variant} className={cn("shrink-0", coinClassName)} />
    <Wordmark className={cn(layout === "vertical" ? "text-2xl" : "text-lg", textClassName)} />
  </span>
);
